import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, MapPin, Phone, MessageCircle, Share2, Facebook,
  Copy, Check, ChevronLeft, ChevronRight, Eye, Clock, Tag,
  ArrowLeft, User, ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "@/components/ui/carousel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

function formatPrice(price) {
  if (!price || price === 0) return "Consultar precio";
  return `L ${price.toLocaleString("es-HN")}`;
}

export default function MarketplaceProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/marketplace/${slug}`);
        setProduct(res.data);
      } catch { /* empty */ } finally { setLoading(false); }
    })();
  }, [slug]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true); toast.success("Enlace copiado");
    setTimeout(() => setCopied(false), 2000);
  };
  const share = (p) => {
    const u = encodeURIComponent(window.location.href);
    const t = encodeURIComponent(`${product?.title} - Marketplace Choluteca`);
    const urls = { facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`, whatsapp: `https://wa.me/?text=${t}%20${u}` };
    window.open(urls[p], "_blank", "width=600,height=400");
  };

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-[3px] border-emerald-600 border-t-transparent" /></div></Layout>
  );

  if (!product) return (
    <Layout>
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <ShoppingBag className="h-16 w-16 text-gray-200 mb-4" />
        <h1 className="text-2xl font-bold text-gray-700 mb-2">Producto no encontrado</h1>
        <Link to="/marketplace"><Button className="rounded-xl mt-4"><ArrowLeft className="h-4 w-4 mr-2" />Volver al Marketplace</Button></Link>
      </div>
    </Layout>
  );

  const photos = product.photo_urls || [];

  return (
    <Layout>
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/marketplace" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4" /> Marketplace
          </Link>
          <Button variant="ghost" size="sm" onClick={() => setShareOpen(true)} data-testid="share-btn">
            <Share2 className="h-4 w-4 mr-1" /> Compartir
          </Button>
        </div>
      </div>

      <section className="py-8 md:py-12 bg-gray-50" data-testid="product-detail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              {photos.length > 0 ? (
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border">
                  <Carousel className="w-full" opts={{ loop: true }}>
                    <CarouselContent className="-ml-0">
                      {photos.map((url, i) => (
                        <CarouselItem key={i} className="pl-0">
                          <div className="aspect-[4/3] cursor-pointer" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}>
                            <img src={getImageUrl(url)} alt={`${product.title} - ${i + 1}`} className="w-full h-full object-contain bg-gray-50" />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {photos.length > 1 && (<><CarouselPrevious className="left-3 bg-white/90 shadow-lg" /><CarouselNext className="right-3 bg-white/90 shadow-lg" /></>)}
                  </Carousel>
                  {photos.length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto">
                      {photos.map((url, i) => (
                        <div key={i} className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 border-transparent hover:border-primary cursor-pointer" onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}>
                          <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-2xl aspect-[4/3] flex items-center justify-center shadow-sm border"><ImageIcon className="h-20 w-20 text-gray-200" /></div>
              )}
              {product.description && (
                <div className="bg-white rounded-2xl p-6 mt-6 shadow-sm border">
                  <h2 className="font-heading text-lg font-bold text-gray-900 mb-3">Descripci&oacute;n</h2>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">{product.description}</p>
                </div>
              )}
              <div className="mt-6"><AdBanner /></div>
            </div>

            <div className="lg:col-span-2 space-y-5">
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm border" data-testid="product-info-card">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`text-xs ${product.condition === "nuevo" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                    {product.condition === "nuevo" ? "Nuevo" : "Usado"}
                  </Badge>
                  <Badge variant="outline" className="text-xs"><Tag className="h-3 w-3 mr-1" />{product.category}</Badge>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-3">{product.title}</h1>
                <p className="text-3xl font-bold text-emerald-700 mb-4">{formatPrice(product.price)}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                  {product.seller_city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{product.seller_city}</span>}
                  <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{product.views} visitas</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(product.created_at).toLocaleDateString("es-HN", { day: "numeric", month: "short" })}</span>
                </div>
                {product.delivery_location && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Entrega:</span> {product.delivery_location}
                  </div>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-6 shadow-sm border" data-testid="seller-card">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><User className="h-4 w-4 text-gray-400" /> Vendedor</h3>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><User className="h-6 w-6 text-emerald-600" /></div>
                  <div>
                    <p className="font-semibold text-gray-900">{product.seller_name}</p>
                    {product.seller_city && <p className="text-sm text-gray-400">{product.seller_city}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  {product.seller_whatsapp && (
                    <a href={`https://wa.me/${product.seller_whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hola, me interesa "${product.title}" en Choluteca.net`)}`} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full rounded-xl h-11 bg-green-600 hover:bg-green-500 text-white" data-testid="whatsapp-btn"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</Button>
                    </a>
                  )}
                  {product.seller_phone && (
                    <a href={`tel:${product.seller_phone}`}>
                      <Button variant="outline" className="w-full rounded-xl h-11" data-testid="call-btn"><Phone className="h-4 w-4 mr-2" />Llamar {product.seller_phone}</Button>
                    </a>
                  )}
                </div>
              </motion.div>
              <Button variant="outline" onClick={() => setShareOpen(true)} className="w-full rounded-xl"><Share2 className="h-4 w-4 mr-2" />Compartir</Button>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Compartir</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={typeof window !== "undefined" ? window.location.href : ""} className="flex-1 px-3 py-2 border rounded-xl text-sm bg-gray-50" />
              <Button onClick={handleCopy} variant="outline" className="rounded-xl">{copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}</Button>
            </div>
            <div className="flex justify-center gap-3">
              <button onClick={() => share("facebook")} className="w-12 h-12 rounded-xl bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center"><Facebook className="h-5 w-5" /></button>
              <button onClick={() => share("whatsapp")} className="w-12 h-12 rounded-xl bg-green-500 text-white hover:bg-green-600 flex items-center justify-center"><MessageCircle className="h-5 w-5" /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 p-2">
          <div className="relative">
            <img src={getImageUrl(photos[lightboxIndex])} alt="" className="w-full max-h-[80vh] object-contain" />
            {photos.length > 1 && (<>
              <button onClick={() => setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="h-6 w-6" /></button>
              <button onClick={() => setLightboxIndex((lightboxIndex + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="h-6 w-6" /></button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-1.5 rounded-full">{lightboxIndex + 1}/{photos.length}</div>
            </>)}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
